import { useEffect, useState } from 'react';
import api from '../../services/api';
import AdminLayout from '../../layouts/AdminLayout';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  mobile: '', company_name: '', trade_name: '', employer_type: 'company',
  gstin: '', pan: '', registration_number: '', contact_person: '',
  designation: '', website: '', address_line: '', pincode: '', description: '',
};

const EMPLOYER_TYPES = [
  ['company', 'Private Company'],
  ['individual', 'Individual / Proprietor'],
  ['government', 'Government / PSU'],
  ['ngo', 'NGO / Trust'],
];

export default function AdminEmployers() {
  const [employers, setEmployers]         = useState([]);
  const [pagination, setPagination]       = useState({});
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');

  const [addModal, setAddModal]           = useState(false);
  const [addForm, setAddForm]             = useState(EMPTY_FORM);
  const [addLoading, setAddLoading]       = useState(false);

  const [editForm, setEditForm]           = useState(null);
  const [editLoading, setEditLoading]     = useState(false);

  const [rejectTarget, setRejectTarget]   = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadEmployers(page = 1) {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('q', search);
      const res = await api.get(`/admin/employers?${params}`);
      setEmployers(res.data.data);
      setPagination(res.data.pagination);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadEmployers(); }, [search]);

  async function handleVerify(id, action, reason = '') {
    setActionLoading(true);
    try {
      await api.post(`/admin/employers/${id}/verify`, { action, reason });
      toast.success(`Employer ${action === 'approve' ? 'verified' : 'rejected'}`);
      setRejectTarget(null);
      loadEmployers();
    } finally { setActionLoading(false); }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setAddLoading(true);
    try {
      await api.post('/admin/employers', addForm);
      toast.success('Employer created and verified');
      setAddModal(false);
      setAddForm(EMPTY_FORM);
      loadEmployers();
    } finally { setAddLoading(false); }
  }

  async function handleEdit(e) {
    e.preventDefault();
    setEditLoading(true);
    try {
      const { _id, _name, mobile, ...body } = editForm;
      await api.put(`/admin/employers/${_id}`, body);
      toast.success('Employer updated');
      setEditForm(null);
      loadEmployers();
    } finally { setEditLoading(false); }
  }

  function openEdit(emp) {
    setEditForm({
      _id: emp.id,
      _name: emp.company_name,
      mobile: emp.mobile,
      company_name: emp.company_name || '',
      trade_name: emp.trade_name || '',
      employer_type: emp.employer_type || 'company',
      gstin: emp.gstin || '',
      pan: emp.pan || '',
      registration_number: emp.registration_number || '',
      contact_person: emp.contact_person || emp.contact_person_name || '',
      designation: emp.designation || '',
      website: emp.website || '',
      address_line: emp.address_line || '',
      pincode: emp.pincode || '',
      description: emp.description || '',
      verification_status: emp.verification_status || 'unverified',
    });
  }

  const F = (label, key, type = 'text', isEdit = false, extra = {}) => {
    const form = isEdit ? editForm : addForm;
    const setForm = isEdit ? setEditForm : setAddForm;
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-600">{label}</label>
        <input type={type} value={form?.[key] ?? ''}
          onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
          {...extra} />
      </div>
    );
  };

  const Sel = (label, key, options, isEdit = false) => {
    const form = isEdit ? editForm : addForm;
    const setForm = isEdit ? setEditForm : setAddForm;
    return (
      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-600">{label}</label>
        <select value={form?.[key] ?? ''}
          onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none">
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    );
  };

  const EmployerForm = ({ isEdit, onSubmit, loading: submitting }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {!isEdit && (
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">Mobile Number *</label>
          <input type="tel" required placeholder="10-digit mobile"
            value={addForm.mobile}
            onChange={(e) => setAddForm((p) => ({ ...p, mobile: e.target.value }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Company Name *', 'company_name', 'text', isEdit, { required: true })}
        {F('Trade Name', 'trade_name', 'text', isEdit)}
      </div>

      {Sel('Employer Type', 'employer_type', EMPLOYER_TYPES, isEdit)}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Contact Person', 'contact_person', 'text', isEdit)}
        {F('Designation', 'designation', 'text', isEdit)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('GSTIN', 'gstin', 'text', isEdit, { placeholder: '15-char GST number' })}
        {F('PAN', 'pan', 'text', isEdit, { placeholder: 'ABCDE1234F' })}
      </div>

      {F('Registration Number', 'registration_number', 'text', isEdit)}
      {F('Website', 'website', 'url', isEdit, { placeholder: 'https://...' })}
      {F('Address', 'address_line', 'text', isEdit)}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {F('Pincode', 'pincode', 'text', isEdit, { maxLength: 6 })}
        {isEdit && Sel('Verification Status', 'verification_status', [
          ['unverified', 'Unverified'],
          ['pending', 'Pending'],
          ['verified', 'Verified'],
          ['rejected', 'Rejected'],
        ], true)}
      </div>

      <div className="space-y-1">
        <label className="block text-xs font-medium text-gray-600">Description</label>
        <textarea
          value={(isEdit ? editForm : addForm)?.description ?? ''}
          onChange={(e) => (isEdit ? setEditForm : setAddForm)((p) => ({ ...p, description: e.target.value }))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20 focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none"
          placeholder="Brief about the company..." />
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="secondary"
          onClick={() => isEdit ? setEditForm(null) : setAddModal(false)}>
          Cancel
        </Button>
        <Button type="submit" fullWidth loading={submitting}>
          {isEdit ? 'Save Changes' : 'Create & Verify Employer'}
        </Button>
      </div>
    </form>
  );

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-gray-900">Employers</h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-500 hidden sm:inline">{pagination.total} total</span>
            <Button onClick={() => { setAddModal(true); setAddForm(EMPTY_FORM); }}>+ Add</Button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <input type="search" placeholder="Search company name, mobile..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none" />
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Company', 'Mobile', 'Type', 'Contact Person', 'GST', 'Verification', 'Registered', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400">Loading...</td></tr>
                ) : employers.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400">No employers found</td></tr>
                ) : employers.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{e.company_name}</td>
                    <td className="px-4 py-3 text-gray-600">{e.mobile}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs capitalize">{e.employer_type || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{e.contact_person || e.contact_person_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs font-mono">{e.gstin || e.gst_number || '—'}</td>
                    <td className="px-4 py-3"><Badge label={e.verification_status} variant={e.verification_status} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5 flex-wrap">
                        <button onClick={() => openEdit(e)}
                          className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg hover:bg-blue-200">
                          Edit
                        </button>
                        {(e.verification_status === 'pending' || e.verification_status === 'unverified') && (
                          <button onClick={() => handleVerify(e.id, 'approve')}
                            className="text-xs bg-green-600 text-white px-2.5 py-1 rounded-lg hover:bg-green-700">
                            ✓ Verify
                          </button>
                        )}
                        {e.verification_status !== 'rejected' && (
                          <button onClick={() => setRejectTarget(e)}
                            className="text-xs bg-red-100 text-red-700 px-2.5 py-1 rounded-lg hover:bg-red-200">
                            ✗ Reject
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {pagination.page} of {pagination.totalPages}</span>
              <div className="flex gap-2">
                {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => loadEmployers(pagination.page - 1)}>← Prev</Button>}
                {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => loadEmployers(pagination.page + 1)}>Next →</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Add Employer Modal ── */}
      {addModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-gray-900">Add New Employer</h3>
                <p className="text-xs text-gray-400 mt-0.5">Account will be created and automatically verified</p>
              </div>
              <button onClick={() => setAddModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[80vh]">
              <EmployerForm isEdit={false} onSubmit={handleAdd} loading={addLoading} />
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Employer Modal ── */}
      {editForm && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h3 className="font-bold text-gray-900">Edit — {editForm._name}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Mobile: {editForm.mobile}</p>
              </div>
              <button onClick={() => setEditForm(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 overflow-y-auto max-h-[80vh]">
              <EmployerForm isEdit onSubmit={handleEdit} loading={editLoading} />
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {rejectTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-gray-900 mb-4">Reject — {rejectTarget.company_name}</h3>
            <textarea id="reject-reason"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 focus:ring-2 focus:ring-brand-500 focus:outline-none"
              placeholder="Reason for rejection (will be sent to employer)..." />
            <div className="flex gap-3 mt-4">
              <Button variant="secondary" onClick={() => setRejectTarget(null)}>Cancel</Button>
              <Button variant="danger" loading={actionLoading}
                onClick={() => handleVerify(rejectTarget.id, 'reject', document.getElementById('reject-reason').value)}>
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
